// src/screens/superadmin/dashboard/Dashboard.jsx
import React, { useContext, useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { FiDollarSign, FiTruck, FiShoppingBag, FiStar } from "react-icons/fi";
import StatCard from "../../../components/admin/statcard/StatCard";
import AdminLayout from "../../../layouts/AdminLayout";
import "../../../assets/css/admin/dashboard/Dashboard.css";
import MyContext from "../../../context/MyContext";
import axios from "axios";
import { AdminDashboardSkeleton } from "../../../components/general/skeleton/SkeletonBlock";

// ════════════════════════════════════════════════════════
//  RecentHelpTickets
// ════════════════════════════════════════════════════════
const RecentHelpTickets = ({ tickets = [] }) => {
  const navigate = useNavigate();
  return (
    <div className="ksa-card">
      <div className="ksa-card-header">
        <div>
          <h3 className="ksa-card-title">Recent Help Tickets</h3>
        </div>
        <Link to="/admin/help-support" className="ksa-action-btn">
          View All tickets
        </Link>
      </div>

      <div className="ksa-table-wrapper">
        <table className="ksa-table">
          <thead>
            <tr>
              <th>USER</th>
              <th>SUBJECT</th>
              <th>STATUS</th>
              <th>PRIORITY</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((ticket) => (
              <tr
                key={ticket.id}
                onClick={() =>
                  navigate(`/admin/help-support?ticket=${ticket.id}`)
                }
                style={{ cursor: "pointer" }}
              >
                <td className="ksa-user-name">{ticket.user}</td>
                <td>{ticket.subject}</td>
                <td>
                  <span className={`ksa-status ksa-status-${ticket.status}`}>
                    {ticket.status}
                  </span>
                </td>
                <td>
                  <span
                    className={`ksa-priority-${ticket.priority.toLowerCase()}`}
                  >
                    {ticket.priority}
                  </span>
                </td>
                <td>
                  <button
                    className="ksa-action-btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/admin/help-support?ticket=${ticket.id}`);
                    }}
                  >
                    {ticket.status === "resolved" ? "View" : "Resolve"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════
//  RecentPayoutRequests
// ════════════════════════════════════════════════════════
const RecentPayoutRequests = ({ payouts = [] }) => {
  const navigate = useNavigate();

  return (
    <div className="ksa-card">
      <div className="ksa-card-header">
        <div>
          <h3 className="ksa-card-title">Recent Payout Requests</h3>
        </div>
        <Link to="/super-admin/payout-requests" className="ksa-action-btn">
          View All Payouts
        </Link>
      </div>

      <div className="ksa-table-wrapper">
        <table className="ksa-table">
          <thead>
            <tr>
              <th>SELLER NAME</th>
              <th>AMOUNT</th>
              <th>DATE</th>
              <th>STATUS</th>
            </tr>
          </thead>
          <tbody>
            {payouts.map((payout) => (
              <tr
                key={payout.id}
                onClick={() =>
                  navigate(`/super-admin/payout-requests?payout=${payout.id}`)
                }
                style={{ cursor: "pointer" }}
              >
                <td className="ksa-user-name">{payout.seller}</td>
                <td className="ksa-amount">{payout.amount}</td>
                <td>{payout.date}</td>
                <td>
                  <span className={`ksa-status ksa-status-${payout.status}`}>
                    {payout.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════
//  LatestBlogs
// ════════════════════════════════════════════════════════
const LatestBlogs = ({ blogs = [] }) => {
  const navigate = useNavigate();

  return (
    <div className="ksa-card">
      <div className="ksa-card-header">
        <div>
          <h3 className="ksa-card-title">Latest Blogs</h3>
        </div>
        <Link to="/admin/blogs" className="ksa-action-btn">
          view all blogs
        </Link>
      </div>

      {blogs.map((blog) => (
        <div
          key={blog.id}
          className="ksa-blog-item"
          onClick={() => navigate(`/admin/blogs?blog=${blog.id}`)}
          style={{ cursor: "pointer" }}
        >
          <div className={`ksa-blog-thumb ${blog.thumbClass}`}></div>
          <div className="ksa-blog-info">
            <h4 className="ksa-blog-title">{blog.title}</h4>
            <p className="ksa-blog-author">{blog.author}</p>
          </div>
          <div className="ksa-blog-date">{blog.date}</div>
        </div>
      ))}
    </div>
  );
};

// ════════════════════════════════════════════════════════
//  TopOrders
// ════════════════════════════════════════════════════════
const TopOrders = ({ orders = [] }) => {
  const navigate = useNavigate();

  return (
    <div className="ksa-card">
      <div className="ksa-card-header">
        <div>
          <h3 className="ksa-card-title">Top Orders</h3>
        </div>
        <Link to="/admin/orders" className="ksa-action-btn">
          view all orders
        </Link>
      </div>

      <div className="ksa-table-wrapper">
        <table className="ksa-table">
          <thead>
            <tr>
              <th>ORDER ID</th>
              <th>CUSTOMER</th>
              <th>AMOUNT</th>
              <th>DATE</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr
                key={order.id}
                onClick={() => navigate(`/admin/orders?order=${order.id}`)}
                style={{ cursor: "pointer" }}
              >
                <td className="ksa-order-id">{order.id}</td>
                <td className="ksa-user-name">{order.customer}</td>
                <td className="ksa-amount">{order.amount}</td>
                <td>{order.date}</td>
                <td>
                  <button
                    className="ksa-action-btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/admin/orders?order=${order.id}`);
                    }}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════
//  TopPerformingSellers
// ════════════════════════════════════════════════════════
const TopPerformingSellers = ({ sellers = [] }) => {
  const navigate = useNavigate();

  return (
    <div className="ksa-card">
      <div className="ksa-card-header">
        <div>
          <h3 className="ksa-card-title">Top Performing Sellers</h3>
          <p className="ksa-card-subtitle">Highest grossing shops</p>
        </div>
        <Link to="/admin/sellers" className="ksa-action-btn">
          View All Sellers
        </Link>
      </div>
      <div className="ksa-table-wrapper">
        <table className="ksa-table">
          <thead>
            <tr>
              <th>SHOP NAME</th>
              <th>CATEGORY</th>
              <th>TOTAL SALES</th>
            </tr>
          </thead>
          <tbody>
            {sellers.map((seller) => (
              <tr
                key={seller.id}
                onClick={() => navigate(`/admin/sellers?seller=${seller.id}`)}
                style={{ cursor: "pointer" }}
              >
                <td className="ksa-user-name">{seller.name}</td>
                <td>{seller.category}</td>
                <td className="ksa-amount">{seller.sales}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════
//  Dashboard (main / default export)
// ════════════════════════════════════════════════════════
const Dashboard = () => {
  const navigate = useNavigate();
  const { baseUrl, authToken } = useContext(MyContext);
  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState({
    monthlyEarnings: 0,
    activeOrders: 0,
    totalProducts: 0,
    customerRating: 0,
  });
  const [tickets, setTickets] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [topOrders, setTopOrders] = useState([]);
  const [topSellers, setTopSellers] = useState([]);


  const getDashboardStats = () => {
    axios
      .get(`${baseUrl}/admin/dashboard`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        const data = response.data;
        setStatsData({
          monthlyEarnings: data.stats?.monthly_earnings ?? 0,
          activeOrders: data.stats?.active_orders ?? 0,
          totalProducts: data.stats?.total_products ?? 0,
          customerRating: data.stats?.customer_rating ?? 0,
        });
        setTickets(data.tickets || []);
        setPayouts(data.payouts || []);
        setBlogs(data.blogs || []);
        setTopOrders(data.top_orders || []);
        setTopSellers(data.top_sellers || []);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/admin/login");
      return;
    }
    getDashboardStats();
  }, [authToken]);

  const stats = [
    {
      icon: FiDollarSign,
      iconBg: "linear-gradient(135deg, #e8f5f0, #d4ede3)",
      iconColor: "#0ac46c",
      label: "MONTHLY EARNINGS",
      value: `₨${Number(statsData.monthlyEarnings).toLocaleString()}`,
    },
    {
      icon: FiTruck,
      iconBg: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
      iconColor: "#2196f3",
      label: "ACTIVE ORDERS",
      value: statsData.activeOrders,
    },
    {
      icon: FiShoppingBag,
      iconBg: "linear-gradient(135deg, #fff3e0, #ffe0b2)",
      iconColor: "#ff9800",
      label: "TOTAL PRODUCTS",
      value: statsData.totalProducts,
    },
    {
      icon: FiStar,
      iconBg: "linear-gradient(135deg, #e1f5fe, #b3e5fc)",
      iconColor: "#03a9f4",
      label: "CUSTOMER RATING",
      value: `${statsData.customerRating} / 5.0`,
    },
  ];

  return (
    <AdminLayout>
      <div>
        {/* Page Header */}
        <div className="ksa-layout-page-header">
          <h1 className="ksa-layout-page-title">Dashboard</h1>
          <p className="ksa-layout-page-subtitle">
            Welcome back, here's what's happening at Kisaan Mart today.
          </p>
        </div>

        {loading ? (
          <AdminDashboardSkeleton />
        ) : (
          <>
            {/* Row 1 — 4 Stat Cards */}
            <Row className="g-4">
              {stats.map((stat, index) => (
                <Col key={index} xs={12} sm={6} xl={3}>
                  <StatCard {...stat} />
                </Col>
              ))}
            </Row>

            {/* Row 2 — Recent Help Tickets (full width) */}
            <Row className="g-4 mt-1">
              <Col xs={12}>
                <RecentHelpTickets tickets={tickets} />
              </Col>
            </Row>

            {/* Row 4 — Recent Payout Requests + Latest Blogs */}
            <Row className="g-4 mt-1">
              <Col xs={12} lg={7}>
                <RecentPayoutRequests payouts={payouts} />
              </Col>
              <Col xs={12} lg={5}>
                <LatestBlogs blogs={blogs} />
              </Col>
            </Row>

            {/* Row 5 — Top Orders + Top Performing Sellers */}
            <Row className="g-4 mt-1">
              <Col xs={12} lg={6}>
                <TopOrders orders={topOrders} />
              </Col>
              <Col xs={12} lg={6}>
                <TopPerformingSellers sellers={topSellers} />
              </Col>
            </Row>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
